import { Component, ElementRef, Input, OnInit, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface CeLoadingSkeletonConfig {
    width?: string;
    height?: string;
    // TODO: Add more theme properties if needed
}

@Component({
    selector: 'ce-skeleton-loader',
    host: {
        ['class']: 'CeSkeletonLoader',
        '[class.CeSkeletonLoader--circular]': 'appearance === "circular"',
        '[class.CeSkeletonLoader--rectangular]': 'appearance === "rectangular"',
    },
    imports: [CommonModule],
    templateUrl: './ce-skeleton-loader.component.html',
    styleUrl: './ce-skeleton-loader.component.scss'
})
export class CeSkeletonLoaderComponent implements OnInit {
    /**
     * The appearance of the skeleton loader.
     *
     * Currently, the skeleton loader supports two appearances: 'rectangular' and 'circular'.
     *
     * Circular appearance adds a border-radius of 50% to the skeleton loader, making it a circle.
     * To make the appearance be perceived as a circle, set the height and width of the skeleton loader to the same value.
     *
     * @default 'rectangular'
     * @type {'rectangular' | 'circular'}
     */
    @Input() appearance: 'rectangular' | 'circular' = 'rectangular';
    /**
     * The configuration object for the skeleton loader.
     *
     * This object allows you to set the width and height of the skeleton loader.
     *
     * @type {CeLoadingSkeletonConfig}
     * @default { height: '100%', width: '100%' }
     * @example { height: '50px', width: '50px' }
     */
    @Input() config: CeLoadingSkeletonConfig = { height: '100%', width: '100%' };

    constructor(private elRef: ElementRef, private renderer: Renderer2) {}

    ngOnInit(): void {
        this.#overrideDefaultStyles();
    }

    #overrideDefaultStyles(): void {
        // Get the class list of the <ce-skeleton-loader/> element
        const classList = Array.from(this.elRef.nativeElement.classList) as string[];

        // Check if the element contains a class that starts with 'u-size-' and ends with '-w' or '-h'.
        // u-size- is a utility class that sets the width and height of an element.
        const hasWidthOverride = classList.some((className: string) => className.startsWith('u-size-') && className.endsWith('-w'));
        const hasHeightOverride = classList.some((className: string) => className.startsWith('u-size-') && className.endsWith('-h'));

        // If the element has a width or height override, remove the default width or height style from the element.
        // Otherwise, set the width or height of the element to the value specified in the config object. If not specified, set it to 'auto'.
        if (hasWidthOverride) {
            this.renderer.removeStyle(this.elRef.nativeElement, 'width');
        } else {
            this.renderer.setStyle(this.elRef.nativeElement, 'width', this.config?.width || 'auto');
        }

        if (hasHeightOverride) {
            this.renderer.removeStyle(this.elRef.nativeElement, 'height');
        } else {
            this.renderer.setStyle(this.elRef.nativeElement, 'height', this.config?.height || 'auto');
        }
    }
}